import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Modal, Box, Typography, Button, Grid, TextField, CircularProgress,
  Paper, Backdrop, Fade, IconButton, InputAdornment, Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AuthContext } from "../../../context/authContext";
import { getCarritoByUserID } from "../../../services/CarritoServices";
import { getClientesPanel } from "../../../services/ClientesServices";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-full)', fontFamily: 'var(--font-body)',
    backgroundColor: 'var(--bg-subtle)', transition: 'all 0.2s ease',
    '& fieldset': { border: '2px solid transparent' },
    '&:hover fieldset': { border: '2px solid var(--border-hover)' },
    '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
    '&.Mui-focused': { backgroundColor: 'var(--bg-card)', boxShadow: '0 0 0 4px rgba(233,69,96,0.1)' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'var(--font-body)', fontSize: '0.88rem' },
};

export const DatatableModal = ({ open, onClose }) => {
  const { setClienteSelected, setIdCarrito, user } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSel, setSearchSel] = useState(user?.id);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);

  const isMobile = window.innerWidth <= 768;

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm, searchSel, page, perPage), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, searchSel, page, perPage]);

  // Fetch
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        setLoading(true);
        const clientes = await getClientesPanel(debouncedSearch, searchSel, page, perPage);
        setData(clientes.usuarios || []);
      } catch (err) {
        console.error(err);
        setError("Error al cargar los clientes");
      } finally { setLoading(false); }
    };
    if (open) fetchClientes();
  }, [debouncedSearch, open]);

  // Filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter((c) =>
      c.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cli_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cli_cuit?.includes(searchTerm)
    );
  }, [data, searchTerm]);

  // Select
  const handleClickSelect = async (cliente) => {
    try {
      setSelectedCliente(cliente);
      localStorage.setItem("clienteSelected", JSON.stringify(cliente));
      setClienteSelected(cliente);
      const resultado = await getCarritoByUserID(cliente.id, true);
      if (resultado?.id) { localStorage.setItem("idCarrito", resultado.id); setIdCarrito(resultado.id); }
      onClose();
    } catch (error) { console.error("Error al seleccionar cliente", error); }
  };

  // Columns
  const columns = [
    {
      field: "razon_social", headerName: "Razón Social", flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: "cli_name", headerName: "Nombre", flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "cli_cuit", headerName: "CUIT", flex: 0.8,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "action", headerName: "Acción", width: 130, sortable: false,
      renderCell: (params) => (
        <Button variant="contained" size="small" onClick={() => handleClickSelect(params.row)}
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
          sx={{
            borderRadius: 'var(--radius-full)', fontFamily: 'var(--font-body)', fontWeight: 700,
            fontSize: '0.7rem', textTransform: 'none', px: 1.5, py: 0.4,
            backgroundColor: 'var(--accent)', boxShadow: 'none',
            '&:hover': { backgroundColor: 'var(--accent-hover)', boxShadow: '0 2px 8px rgba(233,69,96,0.25)' },
          }}>
          Seleccionar
        </Button>
      ),
    },
  ];

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300, sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)' } }}>
      <Fade in={open}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: isMobile ? "94%" : 880, maxHeight: "85vh",
          bgcolor: "var(--bg-card)", boxShadow: 'var(--shadow-xl)',
          borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)',
          overflowY: "auto", display: 'flex', flexDirection: 'column',
        }}>

          {/* ── Header ── */}
          <Box sx={{
            p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid var(--border)', flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PeopleOutlinedIcon sx={{ color: 'var(--accent)', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                  Seleccionar Cliente
                </Typography>
                <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Buscá y seleccioná un cliente para operar
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small"
              sx={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', '&:hover': { backgroundColor: 'var(--bg-subtle)' } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* ── Search ── */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1, flexShrink: 0 }}>
            <TextField
              label="Buscar por nombre o CUIT"
              fullWidth value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--text-muted)', fontSize: 18 }} /></InputAdornment>,
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ width: 24, height: 24 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            {filteredData.length > 0 && !loading && (
              <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.72rem', color: 'var(--text-muted)', mt: 0.75 }}>
                {filteredData.length} cliente{filteredData.length !== 1 ? 's' : ''} encontrado{filteredData.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {/* ── Content ── */}
          <Box sx={{ px: 2.5, pb: 2.5, flex: 1, minHeight: 0 }}>
            {/* Loading */}
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} sx={{ color: 'var(--accent)' }} />
                </Box>
                <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Cargando clientes...</Typography>
              </Box>
            )}

            {/* Error */}
            {error && !loading && (
              <Box sx={{ p: 2, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--danger-bg)', border: '1px solid rgba(239,68,68,0.2)', mt: 1 }}>
                <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--danger)' }}>{error}</Typography>
              </Box>
            )}

            {/* ── MOBILE: Cards ── */}
            {!loading && isMobile && (
              <Box sx={{ maxHeight: '48vh', overflowY: 'auto', pr: 0.5 }}>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {filteredData.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <PersonOutlineIcon
                          sx={{ fontSize: 40, color: 'var(--text-muted)', opacity: 0.4, mb: 1 }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.85rem',
                            color: 'var(--text-muted)',
                          }}
                        >
                          No se encontraron clientes
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    filteredData.map((cliente) => {
                      const isSelected = selectedCliente?.id === cliente.id;
                      return (
                        <Grid item xs={12} key={cliente.id}>
                          <Paper
                            elevation={0}
                            onClick={() => handleClickSelect(cliente)}
                            sx={{
                              border: isSelected
                                ? '2px solid var(--accent)'
                                : '1.5px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              p: 2,
                              cursor: 'pointer',
                              backgroundColor: isSelected
                                ? 'var(--accent-soft)'
                                : 'var(--bg-card)',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'var(--accent)',
                                boxShadow: 'var(--shadow-sm)',
                              },
                            }}
                          >
                            {isSelected && (
                              <Chip
                                label="Seleccionado"
                                size="small"
                                sx={{
                                  mb: 1,
                                  fontFamily: 'var(--font-body)',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  backgroundColor: 'var(--accent)',
                                  color: '#fff',
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                fontFamily: 'var(--font-heading)',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                color: 'var(--text-primary)',
                                mb: 0.25,
                              }}
                            >
                              {cliente.cli_name}
                            </Typography>

                            {cliente.razon_social && (
                              <Typography
                                sx={{
                                  fontFamily: 'var(--font-body)',
                                  fontSize: '0.78rem',
                                  color: 'var(--text-secondary)',
                                  mb: 0.25,
                                }}
                              >
                                {cliente.razon_social}
                              </Typography>
                            )}

                            <Typography
                              sx={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: '0.72rem',
                                color: 'var(--text-muted)',
                              }}
                            >
                              CUIT: {cliente.cli_cuit || '-'}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              </Box>
            )}

            {/* ── DESKTOP: DataGrid ── */}
            {!loading && !isMobile && (
              <Box sx={{ height: 360, mt: 1 }}>
                <DataGrid
                  rows={filteredData}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  disableSelectionOnClick
                  rowHeight={42}
                  sx={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-body)', fontSize: '0.8rem',
                    overflow: 'hidden', backgroundColor: 'var(--bg-card)',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)', minHeight: '42px !important', maxHeight: '42px !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.73rem',
                      color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.03em',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid var(--divider, var(--border))',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: 'var(--bg-subtle)' },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-subtle)', minHeight: '40px !important',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      '&::-webkit-scrollbar': { width: 5 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: 'var(--border-hover)', borderRadius: 'var(--radius-full)' },
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* ── Footer ── */}
          <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={onClose} variant="outlined" size="small"
              sx={{
                borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 700,
                fontSize: '0.82rem', textTransform: 'none', px: 3,
                color: 'var(--text-secondary)', borderColor: 'var(--border)', borderWidth: '1.5px',
                '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
              }}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default DatatableModal;